import * as React from 'react';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { IBaseQuestionProps, TAnswerState } from '../FormContainer/types/internal';
import { RealtorsListingLayout } from '../../components/RealtorsListingLayout';
import { ModalLoader } from '../../components/ModalLoader';
import { ErrorBlock } from '../../components/ErrorBlock';
import { useRealtorListing } from './utils/useRealtorListing';
import { PaginationContainer } from '../PaginationContainer';
import { CounterBannerRealtorsContainer } from '../CounterBannerRealtorsContainer';
import { ListOfRealtorsContainer } from '../ListOfRealtorsContainer';
import { AboutRealtorModalContainer } from '../AboutRealtorModalContainer';
import { useScroll } from './utils/useScroll';
import { SpecificWrapper } from '../../components/SpecificWrapper/SpecificWrapper';
import { getGeoMapper } from '../../utils/getGeoMapper';
import { MAX_REALTORS } from './utils/constants';
import { EDealTypeEx } from '../../repositories/demand/entities/public_v5_create_message/RequestMessageV5Schema';
import { resetCurrentRealtor, setCurrentRealtor } from '../../slices/realtorsSlice';
import { ISpecialistCatalogListing } from '../../types/demand/specialistListing';

export const RealtorsListingContainer: React.FC<React.PropsWithChildren<IBaseQuestionProps>> = props => {
  const {
    question: { answerField },
    setNextStep,
    setAnswersState,
    answersState,
  } = props;

  const dispatch = useDispatch();
  const geoMapper = getGeoMapper(answersState.dealTypeEx?.value as EDealTypeEx);
  const { geo } = geoMapper(answersState.address as TAnswerState);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const resetRealtorCardData = useCallback(() => {
    dispatch(resetCurrentRealtor());
    setIsModalOpen(false);
  }, [dispatch]);

  const setRealtorCardData = useCallback(
    (realtor?: ISpecialistCatalogListing) => {
      if (realtor) {
        setIsModalOpen(true);
        dispatch(setCurrentRealtor(realtor));
      }
    },
    [dispatch],
  );

  const {
    selectedRealtorsId,
    handleSelectRealtor,
    pagination,
    isFetching,
    isError,
    specialistsList,
    handleUnselectRealtor,
    handleSubmit,
    setPageNumber,
    refetchGetSpecialists,
  } = useRealtorListing({
    answerField,
    setNextStep,
    setAnswersState,
    geo,
  });

  const { scrolledElementRef, scrollToTop } = useScroll();

  const isButtonDisabled = selectedRealtorsId.length === MAX_REALTORS;

  if (isFetching) {
    return (
      <SpecificWrapper>
        <ModalLoader />
      </SpecificWrapper>
    );
  }

  if (isError || !specialistsList) {
    return (
      <SpecificWrapper>
        <ErrorBlock
          title="Произошла ошибка"
          text={'Что-то пошло не так. \n Попробуйте еще раз'}
          buttonText="Повторить"
          onClick={refetchGetSpecialists}
        />
      </SpecificWrapper>
    );
  }

  return (
    <>
      <RealtorsListingLayout
        banner={<CounterBannerRealtorsContainer onClick={handleSubmit} countSelected={selectedRealtorsId.length} />}
        list={
          <ListOfRealtorsContainer
            realtors={specialistsList}
            selectedRealtorsId={selectedRealtorsId}
            selectRealtor={handleSelectRealtor}
            unselectRealtor={handleUnselectRealtor}
            onRealtorClick={setRealtorCardData}
            isButtonDisabled={isButtonDisabled}
          />
        }
        pagination={<PaginationContainer scrollToTop={scrollToTop} pagination={pagination} action={setPageNumber} />}
        scrolledElementRef={scrolledElementRef}
      />
      {isModalOpen && (
        <AboutRealtorModalContainer
          resetRealtorCardData={resetRealtorCardData}
          handleSelectRealtor={handleSelectRealtor}
          handleUnselectRealtor={handleUnselectRealtor}
          isButtonDisabled={isButtonDisabled}
          selectedRealtorsId={selectedRealtorsId}
        />
      )}
    </>
  );
};

RealtorsListingContainer.displayName = 'RealtorsListingContainer';
